import React, { FC, useMemo } from 'react';
import {
	getWalkthroughHeatMapColorSet,
	getWalkthroughHeatMapRangeLabel,
	getWalkthroughHeatMapRanges,
	NO_WALKTHROUGH_SCORE_COLOR,
	NO_WALKTHROUGH_SCORE_LABEL,
} from '../../utils/WalkthroughHeatMapRangeUtils';
import { WalkthroughHeatMapRange } from '../../types/walkthrough_heat_map_ranges';

interface WalkthroughHeatMapLegendProps {
	// Unset or unusable falls back to the platform defaults, same as everywhere else.
	ranges?: WalkthroughHeatMapRange[] | null;
	// The grey "No score" chip covers days with no scored walkthrough. It belongs under the heat map
	// itself; a caller listing only the configured colors can drop it.
	includeNoScore?: boolean;
	className?: string;
}

// The swatch-and-range chips rendered under the walkthrough heat map, shared with the site survey
// template page so a template's configured colors are presented the same way in both places.
const WalkthroughHeatMapLegend: FC<WalkthroughHeatMapLegendProps> = ({
	ranges = null,
	includeNoScore = true,
	className = '',
}) => {
	const entries = useMemo(() => {
		const resolvedRanges = getWalkthroughHeatMapRanges(ranges);
		return [
			...(includeNoScore
				? [
						{
							key: 'noScore',
							label: NO_WALKTHROUGH_SCORE_LABEL,
							swatch: NO_WALKTHROUGH_SCORE_COLOR.swatch,
						},
				  ]
				: []),
			...resolvedRanges.map((range, index) => ({
				key: `${range.lowerRange}-${range.color}`,
				label: getWalkthroughHeatMapRangeLabel(range, index, resolvedRanges),
				swatch: getWalkthroughHeatMapColorSet(range.color).swatch,
			})),
		];
	}, [ranges, includeNoScore]);

	return (
		<div className={`flex flex-wrap items-center gap-4 text-sm ${className}`}>
			{entries.map((entry) => (
				<div key={entry.key} className="flex items-center gap-1">
					<div className="h-6 w-6 rounded" style={{ backgroundColor: entry.swatch }} />
					<span>{entry.label}</span>
				</div>
			))}
		</div>
	);
};

export default WalkthroughHeatMapLegend;
